import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface SnapshotRow {
  readonly analyticsId: string;
  readonly metricCategory: string;
  readonly metricKey: string;
  readonly metricValue: string;
  readonly currency: string | null;
  readonly snapshotAt: string;
}

const PAGE = 25;

/**
 * M44 §19 Revenue Analytics Dashboard (§28). Commercial operators review MRR /
 * ARR / churn / renewal metrics derived from VERIFIED transactions (BR-07) and
 * persist immutable snapshots. Viewing needs M44_ANALYTICS_VIEW; taking a
 * snapshot needs M44_ANALYTICS_MANAGE (SoD).
 */
export function RevenueAnalyticsDashboard(): ReactNode {
  const { token } = useAuth();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [metrics, setMetrics] = useState<Record<string, unknown> | null>(null);
  const [rows, setRows] = useState<SnapshotRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    Promise.all([
      apiRequest<{ data: Record<string, unknown> }>('/v1/billing/analytics/revenue', { token }),
      apiRequest<{ data: SnapshotRow[]; total: number }>(`/v1/billing/analytics/snapshots?${params.toString()}`, { token }),
    ])
      .then(([m, s]) => { if (alive) { setMetrics(m.data); setRows(s.data); setTotal(s.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, page, reload]);

  const snapshot = async (): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest('/v1/billing/analytics/snapshot', { method: 'POST', body: {}, token });
      setNotice('Snapshot captured.');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('You are not authorised to snapshot analytics (M44_ANALYTICS_MANAGE required).');
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="revenue-analytics-dashboard">
      <h1>Revenue Analytics Dashboard</h1>

      <section aria-label="revenue-actions">
        <button type="button" aria-label="take-snapshot" onClick={() => void snapshot()}>Take Snapshot</button>
        {actionError && <p role="alert" aria-label="revenue-action-error">{actionError}</p>}
        {notice && <p aria-label="revenue-notice">{notice}</p>}
      </section>

      {error && <p role="alert" aria-label="revenue-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <section aria-label="revenue-metrics">
            <h2>Current Metrics</h2>
            <dl aria-label="revenue-metrics-list">
              {metrics && Object.entries(metrics).map(([k, v]) => (
                <div key={k} data-metric={k}>
                  <dt aria-label={`metric-key-${k}`}>{k}</dt>
                  <dd aria-label={`metric-value-${k}`}>{String(v)}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section aria-label="revenue-snapshots">
            <h2>Snapshots</h2>
            <p aria-label="snapshot-total">{total} snapshot(s).</p>
            <table aria-label="snapshot-table">
              <thead>
                <tr><th scope="col">Category</th><th scope="col">Key</th><th scope="col">Value</th><th scope="col">Currency</th><th scope="col">Captured</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={5}>No snapshots.</td></tr>
                ) : rows.map((s) => (
                  <tr key={s.analyticsId} data-snapshot={s.analyticsId}>
                    <td aria-label={`snapshot-category-${s.analyticsId}`}>{s.metricCategory}</td>
                    <td>{s.metricKey}</td>
                    <td>{s.metricValue}</td>
                    <td>{s.currency ?? '—'}</td>
                    <td>{at(s.snapshotAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <nav aria-label="snapshot-pagination">
              <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
              <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
              <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
            </nav>
          </section>
        </>
      )}
    </main>
  );
}
